import React from 'react';
import { X, Flame, Sparkles, Play, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';

/**
 * LessonModal - Details modal when clicking a lesson node
 */

// Level configurations
const LEVELS = [
  { key: 'iron', label: 'Iron', color: 'from-slate-500 to-slate-600', textColor: 'text-slate-100', icon: '⚔️' },
  { key: 'bronze', label: 'Bronze', color: 'from-amber-600 to-amber-700', textColor: 'text-amber-50', icon: '🛡️' },
  { key: 'silver', label: 'Silver', color: 'from-slate-300 to-slate-400', textColor: 'text-slate-800', icon: '⚡' },
  { key: 'gold', label: 'Gold', color: 'from-yellow-400 to-amber-500', textColor: 'text-yellow-900', icon: '👑' }
];

export default function LessonModal({ 
  isOpen, 
  onClose, 
  lesson,
  onStartLesson 
}) {
  if (!isOpen || !lesson) return null;

  const { 
    lesson_number, 
    title, 
    theme, 
    state,
    description,
    levels_completed = { iron: false, bronze: false, silver: false, gold: false },
    priority = null // Personalization data
  } = lesson;

  const hasStarted = levels_completed.iron;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-md transform transition-all duration-300 ease-out animate-in fade-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow effect behind modal */}
        <div className="absolute inset-0 bg-gradient-to-b from-amber-600/20 to-transparent rounded-2xl blur-xl -z-10" />
        
        {/* Modal content */}
        <div className="bg-gradient-to-b from-slate-800 via-slate-850 to-slate-900 rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative p-6 pb-4 border-b border-slate-700/50">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            
            {/* Lesson title section */}
            <div className="flex items-center gap-4">
              {/* Lesson number badge */}
              <div className={`
                w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold
                shadow-lg border-2
                ${state === 'locked' 
                  ? 'bg-slate-700 border-slate-600 text-slate-500' 
                  : state === 'gold'
                  ? 'bg-gradient-to-br from-yellow-400 to-amber-500 border-yellow-300 text-yellow-900'
                  : state === 'silver'
                  ? 'bg-gradient-to-br from-slate-300 to-slate-400 border-slate-200 text-slate-800'
                  : state === 'bronze'
                  ? 'bg-gradient-to-br from-amber-600 to-amber-700 border-amber-500 text-amber-50'
                  : 'bg-gradient-to-br from-slate-500 to-slate-600 border-slate-400 text-slate-100'
                }
              `}>
                {lesson_number}
              </div>
              
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white">
                  Lesson {lesson_number}
                </h2>
                <p className="text-amber-400/80 flex items-center gap-2">
                  <Flame className="h-4 w-4" />
                  {theme}
                </p>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Description */}
            {description && (
              <p className="text-slate-400 text-sm leading-relaxed">
                {description}
              </p>
            )}
            
            {/* Level progress grid */}
            <div className="grid grid-cols-4 gap-2">
              {LEVELS.map((level) => {
                const isCompleted = levels_completed[level.key];
                
                return (
                  <div 
                    key={level.key}
                    className={`
                      relative text-center p-3 rounded-xl border-2 transition-all duration-300
                      ${isCompleted 
                        ? `bg-gradient-to-br ${level.color} border-transparent shadow-lg` 
                        : 'bg-slate-800/50 border-slate-700/50'
                      }
                    `}
                  >
                    <div className="text-lg mb-1">
                      {level.icon}
                    </div>
                    <div className={`
                      text-[10px] font-bold uppercase tracking-wider
                      ${isCompleted ? level.textColor : 'text-slate-500'}
                    `}>
                      {level.label}
                    </div>
                    
                    {/* Completion indicator */}
                    {isCompleted && (
                      <Sparkles className={`absolute -top-1 -right-1 h-4 w-4 ${level.textColor}`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Personalization breakdown */}
            {priority && priority.breakdown && (
              <div className="bg-purple-900/20 border border-purple-500/20 rounded-xl p-4 space-y-3" data-testid="priority-breakdown">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">Word Analysis</span>
                  {priority.rank && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      priority.rank <= 3 ? 'bg-red-500/20 text-red-300' :
                      priority.rank <= 8 ? 'bg-amber-500/20 text-amber-300' :
                      'bg-emerald-500/20 text-emerald-300'
                    }`}>
                      Priority #{priority.rank}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'New Words', value: priority.breakdown.unknown || 0, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                    { label: 'Weak', value: priority.breakdown.weak || 0, color: 'text-red-400', bg: 'bg-red-500/10' },
                    { label: 'Decaying', value: priority.breakdown.decaying || 0, color: 'text-amber-400', bg: 'bg-amber-500/10' },
                    { label: 'Mastered', value: priority.breakdown.mastered || 0, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                  ].map(stat => (
                    <div key={stat.label} className={`${stat.bg} rounded-lg px-3 py-2 text-center`}>
                      <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
                      <p className="text-[10px] text-slate-400">{stat.label}</p>
                    </div>
                  ))}
                </div>
                {priority.mastery_pct !== undefined && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500"
                        style={{ width: `${Math.round(priority.mastery_pct * 100)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-purple-300 font-medium w-10 text-right">
                      {Math.round(priority.mastery_pct * 100)}%
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {/* Stats row */}
            <div className="flex items-center justify-between py-3 px-4 bg-slate-800/50 rounded-xl text-sm">
              <div className="text-center">
                <p className="text-slate-500 text-xs">Steps</p>
                <p className="text-white font-semibold">4</p>
              </div>
              <div className="w-px h-8 bg-slate-700" />
              <div className="text-center">
                <p className="text-slate-500 text-xs">Questions</p>
                <p className="text-white font-semibold">40</p>
              </div>
              <div className="w-px h-8 bg-slate-700" />
              <div className="text-center">
                <p className="text-slate-500 text-xs">Est. Time</p>
                <p className="text-white font-semibold">~15m</p>
              </div>
            </div>
            
            {/* Action button */}
            <Button 
              className="w-full h-12 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold text-base shadow-lg shadow-amber-600/30 border border-amber-500/30"
              onClick={() => onStartLesson?.(lesson_number)}
            >
              {hasStarted ? (
                <>
                  Continue Lesson
                  <ChevronRight className="h-5 w-5 ml-2" />
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Start Lesson
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Animation styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes zoom-in-95 {
          from { transform: scale(0.95); }
          to { transform: scale(1); }
        }
        .animate-in {
          animation: fade-in 0.2s ease-out, zoom-in-95 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
